// MODULES
import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

// COMPONENTS
import Modal from "@components/Modal/Modal";
import ContentBox from "@components/ContentBox/ContentBox";
import ButtonAdd from "@components/ButtonAdd/ButtonAdd";

// CUSTOM COMPONENTS
import CustomerActionsButton from "@cobComponents/CustomerActionsButton";
import CustomerMenuDrawer from "./CustomerMenuDrawer";
import CustomerInformationCard from "./CustomerInformationCard";
import LastSignedCard from "./LastSignedCard";
import AdvisorsCard from "./AdvisorsCard";
import PoaCard from "./PoaCard";
import ContactDetailsCard from "./ContactDetailsCard";
import UploadAndSignCard from "./UploadAndSignCard";
import BankDetailsCard from "./BankDetailsCard";
import CrmEventsCard from "./CrmEventsCard";
import AddDeleteTagModal from "./AddDeleteTagModal/AddDeleteTagModal";

import DashboardSessions from "../../views/Dashboard/DashboardSessions";
import DashboardPoas from "../../views/Dashboard/DashboardPoas";
import DashboardFutur from "../../views/Dashboard/DashboardFutur/DashboardFutur";
import CustomerNotes from "./CustomerNotes";

// HELPERS
import i18n from "@helpers/i18n";

import useCustomerModal from "./useCustomerModal";
import LogsTable from "./LogsTable";

const CustomerModal = props => {
  const {
    partner,
    isMobileOrTablet,
    isVisible,
    user,
    advisor,
    client,
    allPartnerTags,
    views,
    currentView,
    isAdviceSession,
    handleClose,
    handleViewChange,
    updateCustomers,
    caseFilterContext,
    addCustomerModalOpen,
    toggleAddCustomerModal,
    setCustomerState,
    onWorkflowSwitched,
    nextStepOptions,
    setSelectedCustomer,
    notes,
    setNotes,
    selectedTag,
    addDeleteTagModalOpen,
    toggleAddDeleteTagModal,
    handleTagFilterChange,
    handleTagClick
  } = useCustomerModal(props);

  return (
    <Modal
      className={`Cob-CustomersTable-CustomerModal${!client?.name ? " Modal-title-italic" : ""}`}
      partner={partner}
      visible={isVisible}
      title={client?.name || i18n("customer_information", "no_name_set")}
      subtitle={client.id}
      onClose={handleClose}
    >
      {partner.customerTagsEnabled && (
        <Box
          sx={{
            position: "absolute",
            top: "52px",
            left: partner.name === "fair" ? "280px" : "155px",
            maxWidth: partner.name === "fair" ? "50%" : "58%", 
          }}
        >
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            {client.tags &&
              client.tags.length > 0 &&
              client.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag.charAt(0).toUpperCase() + tag.slice(1)}
                  variant="outlined"
                  onClick={() => {handleTagClick(tag)}}
                  onDelete={() => toggleAddDeleteTagModal("delete", tag)}
                  deleteIcon={<CloseIcon />}
                  sx={{
                    flexDirection: "row",
                    alignItems: "center",
                    height: "22px",
                    padding: 0,
                    marginBottom: "3px !important",
                    backgroundColor: "#d8f4ff",
                    color: "#000",
                    fontSize: "10px",
                    fontWeight: 400,
                    textTransform: "uppercase",
                    border: "none",
                    borderRadius: 1.5,
                    "& .MuiChip-label": {
                      paddingLeft: "7px",
                      paddingRight: "10px"
                    },
                    "& .MuiChip-deleteIcon": {
                      color: "#799eac",
                      fontSize: "11px",
                      "&:hover": { color: "#6394a6ff" }
                    }
                  }}
                />
              ))}
            <Tooltip
              title={
                client.tags && client.tags.length >= 6
                  ? "Maximum 6 taggar tillåtna"
                  : ""
              }
              disableHoverListener={!(client.tags && client.tags.length >= 6)}
            >
              <span>
                <ButtonAdd
                  disabled={client.tags?.length >= 6}
                  label="Tagg"
                  size="small"
                  onClick={() => toggleAddDeleteTagModal("add")}
                  sx={{
                    pt: client.tags?.length > 0 ? "0" : "3px",
                    mb: "2px"
                  }}
                />
              </span>
            </Tooltip>
          </Stack>
        </Box>
      )}
      {!isAdviceSession && (
        <Box
          sx={{
            position: "absolute",
            top: "30px",
            right: "100px",
            height: "32px",
            width: "200px",
            color: "#FFF"
          }}
        >
          <CustomerActionsButton
            user={user}
            client={client}
            partner={partner}
            nextStepOptions={nextStepOptions}
            setSelectedCustomer={setSelectedCustomer}
            setCustomerState={setCustomerState}
            onWorkflowSwitched={onWorkflowSwitched}
            toggleAddCustomerModal={toggleAddCustomerModal}
            cobForm={caseFilterContext?.cobForm}
          />
        </Box>
      )}
      <CloseIcon
        onClick={handleClose}
        sx={{
          position: "absolute",
          top: "15px",
          right: "15px",
          height: "32px",
          width: "32px",
          color: "#FFF",
          backgroundColor: "grey.400",
          borderRadius: "50%",
          padding: "5px",
          cursor: "pointer"
        }}
      />
      {isMobileOrTablet && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "left",
            marginBottom: { xs: 2, md: 4 },
            borderBottom: "5px solid #003d50"
          }}
        >
          <Tabs
            variant="scrollable"
            aria-label="customer tabs"
            value={currentView}
            onChange={handleViewChange}
            scrollButtons={false}
            // allowScrollButtonsMobile
            sx={{
              minHeight: "unset",
              "& .MuiTabs-flexContainer": {
                flexDirection: "row"
              },
              "& .MuiButtonBase-root": {
                fontFamily: "Roboto",
                fontSize: "16px",
                minHeight: "unset",
                py: 0.625,
                px: 2.25,
                color: "#FFF",
                backgroundColor: "#003d50",
                border: 0,
                borderBottom: "5px solid #003d50"
                // transform: "skew(10deg)" /* Equal to skewX(10deg) */
              },
              "& .MuiTabs-scrollButtons.Mui-disabled": {
                opacity: 0.3
              },
              "& .Mui-selected": {
                // color: "#FFF !important",
                color: "#003d50 !important",
                // backgroundColor: "#003d50",
                backgroundColor: "#FFF",
                border: "3px solid #003d50",
                borderBottom: "4px solid #003d50"
              },
              "& .Mui-disabled": {
                backgroundColor: "rgba(0, 63, 82, 0.25)",
                borderBottom: "rgba(0, 63, 82, 0.25)",
                cursor: "not-allowed !important",
                pointerEvents: "auto !important"
              },
              "& .MuiTabs-indicator": {
                display: "none"
              },
              "& .MuiTabs-scroller": {
                alignSelf: "flex-start",
                width: "-webkit-fill-available"
              }
            }}
          >
            {views.map((item, index) => (
              <Tab
                key={index}
                disableRipple
                disableFocusRipple
                label={item.label}
                value={item.name}
                sx={{ alignItems: "flex-start", px: 0, mr: 0.5, minWidth: 60 }}
              />
            ))}
          </Tabs>
        </Box>
      )}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: isMobileOrTablet ? "column" : "row",
          alignItems: "flex-start"
        }}
      >
        {!isMobileOrTablet && (
          <CustomerMenuDrawer
            menuItems={views}
            currentView={currentView}
            totalNotes={notes && notes.length}
            onToggle={() => null}
            handleViewChange={handleViewChange}
          />
        )}
        {currentView === "overview" && (
          <ContentBox
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 18,
              alignItems: "flex-start",
              width: "100%",
              minHeight: "66vh",
              maxHeight: "78vh",
              margin: 0,
              padding: 0,
              boxShadow: "none",
              overflow: "auto"
            }}
          >
            {partner.customerModalWidgets &&
              partner.customerModalWidgets.map((widget, index) => {
                switch (widget) {
                  case "CustomerInformation":
                    return (
                      <CustomerInformationCard
                        key={index}
                        client={client}
                        partner={partner}
                        advisor={advisor}
                        toggleAddCustomerModal={toggleAddCustomerModal}
                        cobForm={caseFilterContext?.cobForm}
                        setCustomerState={setCustomerState}
                        handleModalClose={handleClose}
                      />
                    );
                  case "LastSigned":
                    return <LastSignedCard key={index} client={client} />;
                  case "Advice":
                    return (
                      <AdvisorsCard
                        key={index}
                        client={client}
                        addCustomerModalOpen={addCustomerModalOpen}
                      />
                    );
                  case "Poa":
                    return (
                      <PoaCard
                        key={index}
                        partner={partner}
                        client={client}
                        advisor={advisor}
                        visible={isVisible}
                      />
                    );
                  case "ContactDetails":
                    return (
                      <ContactDetailsCard
                        key={index}
                        client={client}
                        updateCustomers={updateCustomers}
                      />
                    );
                  case "UploadAndSign":
                    return (
                      <UploadAndSignCard
                        key={index}
                        advisor={advisor}
                        client={client}
                      />
                    );
                  case "BankDetails":
                    return (
                      <BankDetailsCard
                        key={index}
                        partner={partner}
                        user={user}
                        client={client}
                      />
                    );
                  case "CrmEvents":
                    // TODO: needs to be enabled conditionally based on partner settings
                    return partner.crmEnabled ? (
                      <CrmEventsCard
                        key={index}
                        partner={partner}
                        client={client}
                      />
                    ) : null;
                  default:
                    return null;
                }
              })}
          </ContentBox>
        )}
        {currentView === "sessions" && (
          <DashboardSessions
            isCustomerModal={true}
            user={user}
            advisor={advisor}
            partner={partner}
            client={client}
            caseFilterContext={caseFilterContext}
            setCustomerState={setCustomerState}
            isAdviceSession={isAdviceSession}
          />
        )}
        {currentView === "futur" && (
          <DashboardFutur
            isCustomerModal={true}
            user={user}
            advisor={advisor}
            partner={partner}
            client={client}
            caseFilterContext={caseFilterContext}
            isAdviceSession={isAdviceSession}
          />
        )}
        {currentView === "poa" && (
          <DashboardPoas
            isCustomerModal={true}
            user={user}
            advisor={advisor}
            partner={partner}
            client={client}
            caseFilterContext={caseFilterContext}
            isAdviceSession={isAdviceSession}
          />
        )}
        {currentView === "notes" && (
          <CustomerNotes
            user={user}
            client={client}
            notes={notes}
            setNotes={setNotes}
            updateCustomers={updateCustomers}
          />
        )}
        {currentView === "logs" && <LogsTable client={client} />}
      </Box>
      {partner.customerTagsEnabled && addDeleteTagModalOpen && (
        <AddDeleteTagModal
          open={addDeleteTagModalOpen}
          tag={selectedTag}
          client={client}
          userName={user.firstName + " " + user.lastName}
          allPartnerTags={allPartnerTags}
          toggle={toggleAddDeleteTagModal}
          updateCustomers={updateCustomers}
          handleTagFilterChange={handleTagFilterChange}
        />
      )}
    </Modal>
  );
};

export default CustomerModal;
